/**
 * CSS Chunk Loader - Data-Attribute-Driven Lazy CSS Loading
 *
 * This module automatically loads CSS chunks based on data-load-css attributes in the DOM.
 * It provides a simple way to lazy-load feature-specific stylesheets without JavaScript chunks.
 *
 * APPROACH:
 * 1. SCSS files are defined as entry points in webpack.scss-entries.js
 * 2. Webpack compiles them to content-hashed /dist/css/chunks/*.<hash>.min.css files
 *    and emits /dist/css-chunk-manifest.json mapping each entry key to its hashed path
 * 3. Razor views add data-load-css attributes to components
 * 4. This loader reads the manifest and injects <link> tags with the hashed href
 *
 * USAGE IN RAZOR VIEWS:
 * Add the data-load-css attribute with the entry key name from webpack.scss-entries.js:
 *
 * @example
 * // 1. Define entry in webpack.scss-entries.js:
 * module.exports = {
 *     "media-gallery-styles": "Features/MediaGallery/styles/media-gallery-vanilla.scss"
 * };
 *
 * // 2. Add attribute in Razor view:
 * <div class="media-gallery" data-load-css="media-gallery-styles">
 *     <!-- component content -->
 * </div>
 *
 * // 3. This loader looks up the hashed path in css-chunk-manifest.json and injects:
 * <link rel="stylesheet" href="/dist/css/chunks/media-gallery-styles.a1b2c3d4.min.css">
 *
 * WEBPACK OUTPUT:
 * Entry key "media-gallery-styles" outputs to:
 * /wwwroot/dist/css/chunks/media-gallery-styles.<hash>.min.css
 *
 * DUPLICATE PREVENTION:
 * - Tracks loaded chunks in memory (loadedChunks Set)
 * - Checks DOM for existing <link> tags before injecting
 *
 * BENEFITS:
 * - No JS chunks loaded (CSS-only)
 * - Selective loading (only loads CSS for components present on page)
 * - Simple and explicit (no webpack dynamic import magic)
 * - Leverages existing webpack infrastructure
 */

/* eslint-disable no-undef */

const loadedChunks = new Set();

// The manifest maps each data-load-css chunk name to its content-hashed path
// (relative to /dist/). It is emitted by the BundleAssetManager plugin in
// webpack.common.js. Fetched once and cached for the lifetime of the page.
let manifestPromise = null;

function getCssChunkManifest() {
  if (!manifestPromise) {
    manifestPromise = fetch('/dist/css-chunk-manifest.json')
      .then((response) => (response.ok ? response.json() : {}))
      .catch(() => ({}));
  }
  return manifestPromise;
}

document.addEventListener('DOMContentLoaded', function () {
  const elements = document.querySelectorAll('[data-load-css]');

  if (!elements.length) {
    return;
  }

  getCssChunkManifest().then((manifest) => {
    elements.forEach((el) => {
      const chunkName = el.dataset.loadCss;

      // Skip if already loaded or no chunk name provided
      if (!chunkName || loadedChunks.has(chunkName)) {
        return;
      }

      // Mark as loaded to prevent duplicate processing
      loadedChunks.add(chunkName);

      // Resolve the content-hashed path from the manifest. Fall back to the
      // legacy un-hashed path if the manifest is missing the entry.
      const manifestPath = manifest[chunkName];
      const href = manifestPath ? `/dist/${manifestPath}` : `/dist/css/chunks/${chunkName}.min.css`;

      // Check if stylesheet is already loaded in DOM
      if (!document.querySelector(`link[href^="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        // Let components re-measure layout once their styles apply
        link.addEventListener('load', () => {
          document.dispatchEvent(new CustomEvent('css-chunk:loaded', { detail: { chunkName } }));
        });
        document.head.appendChild(link);
      }
    });
  });
});
